import React, { useMemo } from "react";
import { Box, Stack, Typography } from "@mui/material";
import {
    DocumentQuestion,
    DocumentAnalyzedObject,
    DocumentAnswerType
} from "./documentQuestionsApi";

interface PrintPreviewQuestionsObjectsViewProps {
    questions: DocumentQuestion[];
    analyzedObjects: DocumentAnalyzedObject[];
    lang?: "pl" | "en";
}

const T = (lang: "pl" | "en") =>
    lang === "pl"
        ? {
            defs: "Definicje pytań",
            objs: "Analizowane obiekty",
            lp: "Lp.",
            question: "Pytanie",
            type: "Typ",
            object: "Obiekt",
            noQuestions: "Brak pytań w tej sekcji.",
            noObjects: "Brak obiektów do wyświetlenia.",
            print: "Drukuj",
            close: "Zamknij",
            legendYes: "✓ = Tak",
            legendNo: "✗ = Nie",
            legendNA: "N/D = Nie dotyczy",
            legendNone: "— = Brak",
        }
        : {
            defs: "Question definitions",
            objs: "Analyzed objects",
            lp: "No.",
            question: "Question",
            type: "Type",
            object: "Object",
            noQuestions: "No questions.",
            noObjects: "No objects to display.",
            print: "Print",
            close: "Close",
            legendYes: "✓ = Yes",
            legendNo: "✗ = No",
            legendNA: "N/A = Not applicable",
            legendNone: "— = None",
        };

function answerToSymbol(v: DocumentAnswerType): string {
    switch (v) {
        case "YES": return "✓";
        case "NO": return "✗";
        case "NA": return "N/D";
        case "NONE":
        default: return "—";
    }
}



export const PrintPreviewQuestionsObjectsView: React.FC<
    PrintPreviewQuestionsObjectsViewProps
> = ({ questions, analyzedObjects, lang = "pl" }) => {
    const t = T(lang);
    const sortedQuestions = useMemo(
        () => [...(questions ?? [])].sort((a, b) => a.order - b.order),
        [questions]
    );
    const totalCols = 3 + (sortedQuestions?.length ?? 0);
    const hasObjects = (analyzedObjects?.length ?? 0) > 0;


    const items = [
        { sym: "✓", text: lang === "pl" ? "Tak" : "Yes", color: "success.main" },
        { sym: "✗", text: lang === "pl" ? "Nie" : "No", color: "error.main" },
        { sym: lang === "pl" ? "N/D" : "N/A", text: lang === "pl" ? "Nie dotyczy" : "Not applicable" },
        { sym: "—", text: lang === "pl" ? "Brak" : "None" },
    ];

    return (
        <Box
            sx={{
                backgroundColor: "#fff",
                p: 2,
                border: "1px solid #ddd",
                fontFamily: `Arial, "Helvetica Neue", Helvetica, sans-serif`,
                lineHeight: 1.6,
                color: "#000",
                "& h1": { fontSize: "1.25rem", margin: "0 0 8px 0" },
                "& section": { pageBreakInside: "avoid" },
                "& table": {
                    width: "100%",
                    borderCollapse: "collapse",
                    marginBottom: "16px",
                    pageBreakInside: "avoid",
                },
                "& th, & td": {
                    border: "1px solid #000",
                    padding: "6px 8px",
                    verticalAlign: "top",
                    fontSize: "0.9rem",
                    wordBreak: "break-word",
                },
                "& th": { backgroundColor: "#f0f0f0", fontWeight: 700, textAlign: "left" },
                "@media print": { border: "none", p: 0 },
                "& .legendYes": { color: "#2e7d32", fontWeight: 700 },
                "& .legendNo": { color: "#d32f2f", fontWeight: 700 },

            }}
        >
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1,
                    mb: 2,
                    "@media print": { display: "none" },
                }}
            >
            </Box>
            <section>
                <table aria-label="question-definitions">
                    <thead>
                        <tr>
                            <th colSpan={3} style={{ backgroundColor: "#eaeaea", fontWeight: 700, fontSize: "1rem" }}>
                                {t.defs}
                            </th>
                        </tr>
                        <tr>
                            <th style={{ width: 56 }}>{t.lp}</th>
                            <th>{t.question}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedQuestions.length > 0 ? (
                            sortedQuestions.map((q) => (
                                <tr key={q.id}>
                                    <td>{q.order}</td>
                                    <td>{q.text}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={3} style={{ fontStyle: "italic", color: "#777" }}>
                                    {t.noQuestions}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </section>
            <div style={{ pageBreakAfter: "always", margin: "8px 0" }} />
            <section>
                <table aria-label="analyzed-objects">
                    <thead>
                        <th colSpan={totalCols} style={{ backgroundColor: "#eaeaea", fontWeight: 700, fontSize: "1rem" }}>
                            {t.objs}
                        </th>
                        <tr>
                            <th style={{ width: 56 }}>{t.lp}</th>
                            <th>{t.object}</th>
                            {sortedQuestions.map((q) => (
                                <th key={q.id} style={{ width: 48, textAlign: "center" }}>
                                    {q.order}
                                </th>
                            ))}
                            <th style={{ width: 64, textAlign: "center" }}>Ocena</th>
                        </tr>
                    </thead>
                    <tbody>
                        {hasObjects ? (
                            analyzedObjects.map((obj, idx) => (
                                <React.Fragment key={obj.id ?? idx}>
                                    <tr style={{ breakInside: "avoid" as any }}>
                                        <td>{idx + 1}</td>
                                        <td>{obj.name}</td>

                                        {sortedQuestions.map((q) => {
                                            const ans =
                                                obj.answers?.find(a => String(a.questionId) === String(q.id))?.value ?? "NONE";
                                            return (
                                                <td
                                                    key={q.id}
                                                    style={{
                                                        textAlign: "center",
                                                        fontWeight: 700,
                                                        color:
                                                            ans === "YES" ? "#2e7d32" :
                                                                ans === "NO" ? "#d32f2f" :
                                                                    undefined
                                                    }}
                                                >
                                                    {answerToSymbol(ans)}
                                                </td>
                                            );
                                        })}

                                        <td style={{ width: 64, textAlign: "center" }}>
                                            {answerToSymbol(obj.result)}
                                        </td>
                                    </tr>

                                    {obj.resultComment?.trim() && (
                                        <tr style={{ breakInside: "avoid" as any }}>
                                            <td />
                                            <td
                                                colSpan={totalCols - 1}
                                                style={{
                                                    fontSize: "0.85rem",
                                                    color: "#555",
                                                    background: "#fafafa",
                                                    whiteSpace: "pre-wrap",
                                                    wordBreak: "break-word",
                                                    paddingTop: 6,
                                                    paddingBottom: 6
                                                }}
                                            >
                                                <strong>Komentarz:</strong> {obj.resultComment}
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={totalCols} style={{ fontStyle: "italic", color: "#777" }}>
                                    {t.noObjects}
                                </td>
                            </tr>
                        )}
                    </tbody>

                </table>
                <Box sx={{ fontSize: 12, color: "text.secondary" }}>
                    {items.map((it, i) => (
                        <React.Fragment key={i}>
                            <Box component="span" sx={{ fontWeight: 700, color: it.color }}>
                                {it.sym}
                            </Box>{" "}
                            {it.text}
                            {i < items.length - 1 ? "; " : ""}
                        </React.Fragment>
                    ))}
                </Box>
            </section>
        </Box>
    );
};